/**
 * Links in a bot answer. They are taken out of the escaped text before the markdown pass, so `_`,
 * `*` and `---` inside a URL aren't read as markdown, and put back as anchors at the end. A link
 * whose visible text is a long URL is drawn as a compact "Open link" button with the site's name,
 * instead of a URL wrapping over several lines of the bubble.
 */

/** A bare URL longer than this becomes an Open link button. */
export const LONG_LINK_CHARS = 40

const token = (index: number) => `${index}`
const TOKEN_RE = /(\d+)/g

const IMAGE_RE = /!\[([^\]]*)\]\(([^)\s]+)\)/g
const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g
const BARE_URL_RE = /(?:https?:\/\/|www\.)[^\s"'<>]+/gi
/**
 * A URL the answer broke after its `?` or `&` ("…/edit? tab=t.0", or a line break there): the
 * query parameter after the gap still belongs to it.
 */
const SPLIT_QUERY_RE = /((?:https?:\/\/|www\.)[^\s"'<>]*(?:\?|&amp;))\s+([\w.~-]+=[^\s"'<>]*)/gi

/** lucide external-link, inheriting the link colour. */
const EXTERNAL_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>'

/** The text is already HTML-escaped (`&`, `<`, `>`); this turns it back into plain text. */
const unescape = (text: string) =>
  text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const escapeAttr = (text: string) => escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;')

/**
 * Splits sentence punctuation off the end of a bare URL ("see https://x.com/a."). A closing `)` is
 * kept when the URL opened one (Wikipedia-style links).
 */
function trimUrl(url: string): [string, string] {
  let end = url.length
  for (;;) {
    const rest = url.slice(0, end)
    if (rest.endsWith('&gt;')) {
      end -= 4
      continue
    }
    const last = rest.at(-1)
    if (!last || (!".,;:!?*_]'".includes(last) && last !== ')')) break
    if (last === ')' && (rest.match(/\(/g)?.length ?? 0) >= (rest.match(/\)/g)?.length ?? 0)) break
    end -= 1
  }
  return [url.slice(0, end), url.slice(end)]
}

/** A link target safe to put in `href` (web, mail, phone or same-site), as plain text; else null. */
function safeTarget(escapedHref: string): string | null {
  const href = unescape(escapedHref).trim()
  if (/^www\./i.test(href)) return `https://${href}`
  if (/^(https?:|mailto:|tel:)/i.test(href) || /^[/#]/.test(href)) return href
  return null
}

const hostOf = (href: string) => {
  try {
    return new URL(href).hostname.replace(/^www\./i, '')
  } catch {
    return ''
  }
}

/** `**bold**` / `*italic*` in a link's text (the markdown pass doesn't reach inside links). */
const inlineEmphasis = (label: string) =>
  label
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')

/**
 * The anchor for one link. `escapedLabel` and `escapedHref` come from the escaped text. A label that
 * is itself a long URL becomes the Open link button; an unsafe target leaves just the label.
 */
function anchor(escapedLabel: string, escapedHref: string, openLink: string): string {
  const target = safeTarget(escapedHref)
  if (!target) return inlineEmphasis(escapedLabel)
  const href = escapeAttr(target)
  const label = unescape(escapedLabel).trim()
  if (/^(https?:\/\/|www\.)\S+$/i.test(label) && label.length > LONG_LINK_CHARS) {
    const host = hostOf(target)
    return (
      `<a class="chatbot-link-button" href="${href}" target="_blank" rel="noopener noreferrer" title="${escapeAttr(label)}">` +
      // No <span>: BotMessage paints spans in the body text colour, over the link colour.
      `${EXTERNAL_ICON}${escapeHtml(openLink)}` +
      (host ? `<small class="chatbot-link-button__host">${escapeHtml(host)}</small>` : '') +
      '</a>'
    )
  }
  return `<a href="${href}" target="_blank" rel="noopener noreferrer">${inlineEmphasis(escapedLabel)}</a>`
}

/**
 * Replaces the images, markdown links and bare URLs in HTML-escaped text with placeholders the
 * markdown pass leaves alone. `restore` swaps them for their HTML once that pass is done.
 * `openLink` is the button text for a long URL.
 */
export function extractLinks(
  escaped: string,
  openLink: string
): {text: string; restore: (html: string) => string} {
  const parts: string[] = []
  const keep = (html: string) => {
    parts.push(html)
    return token(parts.length - 1)
  }

  const text = escaped
    .replace(SPLIT_QUERY_RE, '$1$2')
    .replace(IMAGE_RE, (whole, alt: string, src: string) => {
      const target = safeTarget(src)
      return target && /^https?:/i.test(target)
        ? keep(`<img alt="${escapeAttr(unescape(alt))}" src="${escapeAttr(target)}" style="max-width:100%;"/>`)
        : alt
    })
    .replace(MARKDOWN_LINK_RE, (whole, label: string, href: string) => keep(anchor(label, href, openLink)))
    .replace(BARE_URL_RE, (match: string) => {
      const [url, after] = trimUrl(match)
      return keep(anchor(url, url, openLink)) + after
    })

  return {
    text,
    restore: (html) => html.replace(TOKEN_RE, (whole, index: string) => parts[Number(index)] ?? ''),
  }
}
